import type { GetServerSidePropsContext } from 'next';
import type { Region } from '@kc-console/context';

type GetRequestUrlDataResponse = {
  /**
   * domain_id 쿼리 파라미터 값 (없으면 빈 문자열)
   */
  domainId: string;
  /**
   * `${origin}${basePath}${resolvedUrl}`
   * basePath는 middleware의 X-Base-Path 헤더에서 추출 (없으면 빈 문자열)
   */
  href: string;
  /**
   * project_id 쿼리 파라미터 값 (없으면 빈 문자열)
   */
  projectId: string;
  /**
   * region 쿼리 파라미터 값 (없으면 빈 문자열)
   */
  region: Region;
  /**
   * https://${host}${basePath}${resolvedUrl} 기반으로 생성된 URL 객체
   * basePath는 middleware의 X-Base-Path 헤더에서 추출 (없으면 빈 문자열)
   */
  urlObj: URL;
  /**
   * urlObj.search 기반으로 생성된 URLSearchParams 객체
   */
  urlSearchParamsObj: URLSearchParams;
  /**
   * context: GetServerSidePropsContext 내 req.headers.cookie 값
   */
  cookie?: string;
};

/**
 * - GetServerSideProps 의 context 를 이용하여 url, query, cookie 관련 정보를 추출하는 함수
 * - SSR 에서는 window.location 에서 추출하던 origin, protocol, query 등의 데이터를 바로 얻을 수 없기 때문에 URL & URLSearchParms 를 이용
 * @param context
 * @example
const byebyeGuard: GuardType = async chain => {
  const ctx = chain.getContext();
  const { urlObj } = getRequestUrlData(ctx); // 서버사이드에서 사용.
  const { pathname } = urlObj;
  const isByeService = pathname.startsWith('/bye');

  if (!isByeService) return chain.resolve(404);

  return chain.callNext();
};
 */
export const getRequestUrlData = (
  context: GetServerSidePropsContext,
): GetRequestUrlDataResponse => {
  const { req, resolvedUrl } = context;
  const { host, cookie } = req.headers;
  const basePath = (req.headers['x-base-path'] as string) || '';
  const urlObj = new URL(`https://${host}${basePath}${resolvedUrl}`);
  const urlSearchParamsObj = new URLSearchParams(urlObj.search);

  return {
    urlObj,
    urlSearchParamsObj,
    href: `${urlObj.origin}${basePath}${resolvedUrl}`,
    cookie,
    projectId: urlSearchParamsObj.get('project_id') ?? '',
    domainId: urlSearchParamsObj.get('domain_id') ?? '',
    region: (urlSearchParamsObj.get('region') as Region) ?? '',
  };
};
